import {
  BarChart3,
  BadgeDollarSign,
  BellRing,
  Brain,
  Gauge,
  History,
  Images,
  KeyRound,
  Megaphone,
  MessageSquare,
  Boxes,
  Send,
  ShieldCheck,
  ShieldAlert,
  Users,
  WandSparkles,
  ChevronsUpDown,
  LogOut,
  Settings2,
  LibraryBig,
} from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { NavLink, useNavigate } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

import { BrandMark } from "./BrandMark";

// Bot management is a Telegram concept — it lives inside the Telegram page.
const userGroups = [
  {
    label: "Общение",
    items: [
      { to: "/", label: "Чат", icon: MessageSquare },
      { to: "/character", label: "Характер", icon: WandSparkles },
      { to: "/characters", label: "Каталог персонажей", icon: LibraryBig },
    ],
  },
  {
    label: "Ваше пространство",
    items: [
      { to: "/history", label: "История", icon: History },
      { to: "/gallery", label: "Галерея", icon: Images },
      { to: "/reminders", label: "Напоминания", icon: BellRing },
      { to: "/memory", label: "Память", icon: Brain },
    ],
  },
  {
    label: "Аккаунт",
    items: [
      { to: "/telegram", label: "Telegram", icon: Send },
      { to: "/partners", label: "Партнёрам", icon: Users },
      { to: "/usage", label: "Подписка", icon: BarChart3 },
      { to: "/pricing", label: "Тарифы и кредиты", icon: BadgeDollarSign },
    ],
  },
];

const adminItems = [
  { to: "/admin-panel", label: "Обзор", icon: Gauge },
  {to:"/admin-panel/finance",label:"Поступления и расходы",icon:BadgeDollarSign},
  {to:"/admin-panel/taxes",label:"Налоговый учёт",icon:History},
  { to: "/admin-panel/partners", label: "Партнёры и выплаты", icon: Users },
  { to: "/admin-panel/users", label: "Пользователи", icon: Users },
  { to: "/admin-panel/registrations", label: "Новые пользователи", icon: BarChart3 },
  { to: "/admin-panel/memory-quality", label: "Качество памяти", icon: ShieldAlert },
  { to: "/admin-panel/invites", label: "Инвайты", icon: KeyRound },
  { to: "/admin-panel/models", label: "Модели", icon: Boxes },
  { to: "/admin-panel/broadcasts", label: "Рассылки", icon: Megaphone },
];

/**
 * Shared nav markup used by both the desktop rail (left aside) and the
 * mobile drawer (Sheet). The mobile variant receives an explicit callback:
 * wrapping React Router's dynamic `className` in Radix `asChild` serialises
 * that function and makes the links visually disappear.
 */
function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const { role } = useAuth();
  const isSuper = role === "superadmin";

  return (
    <>
      {userGroups.map((group, index) => (
        <div key={group.label} className={index === 0 ? "" : "mt-6"}>
          <SectionLabel>{group.label}</SectionLabel>
          <ul className="space-y-1">
            {group.items.map(({ to, label, icon: Icon }) => (
              <NavItem
                key={to}
                to={to}
                end={to === "/"}
                label={label}
                icon={Icon}
                onNavigate={onNavigate}
              />
            ))}
          </ul>
        </div>
      ))}

      {isSuper && (
        <>
          <div className="mt-6">
            <SectionLabel>
              <ShieldCheck className="mr-1.5 inline h-3 w-3" /> Суперадмин
            </SectionLabel>
          </div>
          <ul className="space-y-1">
            {adminItems.map(({ to, label, icon: Icon }) => (
              <NavItem
                key={to}
                to={to}
                end={to === "/admin-panel"}
                label={label}
                icon={Icon}
                onNavigate={onNavigate}
              />
            ))}
          </ul>
        </>
      )}
    </>
  );
}

/** Desktop product rail. The dark forest surface mirrors the public landing. */
export function Sidebar() {
  return (
    <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 overflow-hidden bg-brand-forest text-white md:flex md:flex-col lg:w-[280px]">
      <div className="flex h-20 items-center px-5 lg:px-6">
        <Brand />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-6 lg:px-4">
        <NavList />
      </nav>
      <SidebarAccount />
    </aside>
  );
}

/** Mobile drawer contents — same nav rendered inside a Sheet. */
export function MobileSidebarContents({ onNavigate }: { onNavigate: () => void }) {
  return (
    <div className="flex h-full flex-col bg-brand-forest text-white">
      <div className="flex h-20 items-center px-5">
        <Brand />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-6">
        <NavList onNavigate={onNavigate} />
      </nav>
      <SidebarAccount onNavigate={onNavigate} />
    </div>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-3">
      <BrandMark className="h-10 w-10" />
      <div className="flex flex-col leading-tight">
        <span className="font-extrabold tracking-[-0.04em] text-white">MyAiFriend</span>
        <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/45">
          Личный кабинет
        </span>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.14em] text-white/35">
      {children}
    </div>
  );
}

function NavItem({
  to,
  end,
  label,
  icon: Icon,
  onNavigate,
}: {
  to: string;
  end?: boolean;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onNavigate?: () => void;
}) {
  const link = (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "group flex min-h-10 items-center gap-3 rounded-xl px-3 py-2 text-sm transition-all duration-200",
          isActive
            ? "bg-brand-lime font-bold text-brand-forest shadow-[0_8px_24px_rgba(216,255,114,0.12)]"
            : "font-medium text-white/58 hover:bg-white/[0.07] hover:text-white"
        )
      }
    >
      <Icon className="h-[18px] w-[18px] transition-transform group-hover:scale-105" />
      {label}
    </NavLink>
  );
  return <li>{link}</li>;
}

function SidebarAccount({ onNavigate }: { onNavigate?: () => void }) {
  const { email, role, logout } = useAuth();
  const navigate = useNavigate();
  const closeAndNavigate = () => onNavigate?.();
  const signOut = () => {
    logout();
    onNavigate?.();
    navigate("/login");
  };

  return (
    <div className="border-t border-white/10 p-4">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button className="group flex w-full min-w-0 items-center gap-3 rounded-2xl bg-white/[0.055] p-3 text-left outline-none transition hover:bg-white/[0.09] data-[state=open]:bg-white/[0.11]">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white/10 text-xs font-bold text-brand-lime">
              {(email?.[0] ?? "M").toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-white/85">{email ?? "MyAiFriend"}</p>
              <p className="mt-0.5 text-[10px] uppercase tracking-[0.12em] text-white/35">
                {role === "superadmin" ? "Суперадмин" : role === "admin" ? "Администратор" : "Пользователь"}
              </p>
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-white/30 transition group-hover:text-white/60" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            side="top"
            align="start"
            sideOffset={10}
            className="z-50 w-[min(248px,calc(100vw-2rem))] overflow-hidden rounded-2xl border bg-popover p-1.5 text-popover-foreground shadow-2xl data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
          >
            <div className="px-3 py-2">
              <div className="truncate text-xs font-bold">{email ?? "MyAiFriend"}</div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">Настройки аккаунта</div>
            </div>
            <DropdownMenu.Separator className="my-1 h-px bg-border" />
            <DropdownMenu.Item asChild>
              <NavLink
                to="/settings"
                onClick={closeAndNavigate}
                className="flex cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold outline-none hover:bg-accent focus:bg-accent"
              >
                <Settings2 className="h-4 w-4" /> Настройки
              </NavLink>
            </DropdownMenu.Item>
            <DropdownMenu.Item asChild>
              <NavLink
                to="/character"
                onClick={closeAndNavigate}
                className="flex cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold outline-none hover:bg-accent focus:bg-accent"
              >
                <WandSparkles className="h-4 w-4" /> Характер ассистента
              </NavLink>
            </DropdownMenu.Item>
            <DropdownMenu.Separator className="my-1 h-px bg-border" />
            <DropdownMenu.Item
              onSelect={signOut}
              className="flex cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-destructive outline-none hover:bg-destructive/10 focus:bg-destructive/10"
            >
              <LogOut className="h-4 w-4" /> Выйти
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
